import type { HTMLAttributes, ReactNode, Ref } from 'react'
import { cx, isFilled } from '@/shared/lib'
import { Skeleton } from '@/shared/ui/Skeleton'
import s from './PageHeader.module.css'

/**
 * Уровень заголовка. `h1` — экран целиком, `h2`…`h4` — раздел внутри экрана.
 * Ветки «без заголовка» нет: шапка без заголовка — не шапка.
 */
export type PageHeaderLevel = 1 | 2 | 3 | 4

/**
 * Громкость шапки: `page` — название экрана (clamp 21…28px),
 * `section` — заголовок раздела внутри экрана (20px).
 */
export type PageHeaderVariant = 'page' | 'section'

export type PageHeaderProps = {
  /**
   * Название экрана. Не `title`: то занято нативным тултипом и уходит в `...rest` —
   * та же причина, что у `heading` в `Card`, `Alert` и `Modal`.
   */
  heading: ReactNode
  /** `h1`…`h4`. Уровень зависит от места вставки, поэтому выбирает его потребитель. */
  level?: PageHeaderLevel
  variant?: PageHeaderVariant
  /** Строка над заголовком: `SectionCaption` с именем модуля, текстовая крошка «Каталог · Серверы». */
  eyebrow?: ReactNode
  /** Сводка под заголовком: «4 позиции · 8 413 600 ₽ · ТК „Деловые линии“». */
  description?: ReactNode
  /** Состояние объекта: `StatusBadge` черновика, шага, связи с 1С, мета-капсула раздела. */
  status?: ReactNode
  /** Ряд контролов справа: `Button`, `Select` склада, `SegmentedControl` вида. */
  actions?: ReactNode
  /** Полоса под шапкой: чипы снятых фильтров, шаги мастера, вкладки, инсет-блок периода. */
  children?: ReactNode
  /** Объект ещё грузится: вместо заголовка и описания — заглушки той же геометрии. */
  loading?: boolean
  /** `id` заголовка: по нему `<main>` получает имя через `aria-labelledby`. */
  headingId?: string
  /** Корневой `<header>`. */
  ref?: Ref<HTMLElement>
} & Omit<HTMLAttributes<HTMLElement>, 'children'>

// Карта объявлена вне рендера: тег не создаётся заново на каждую отрисовку
const HEADING_TAG: Record<PageHeaderLevel, 'h1' | 'h2' | 'h3' | 'h4'> = {
  1: 'h1',
  2: 'h2',
  3: 'h3',
  4: 'h4',
}

// CSS-модуль типизирован индексной сигнатурой, поэтому значения — string | undefined
const VARIANT_CLASS: Record<PageHeaderVariant, string | undefined> = {
  page: s.page,
  section: s.section,
}

/**
 * Шапка рабочего экрана: подпись раздела, заголовок, сводка, статус объекта и ряд действий.
 * Единственное место, где страница называет себя вслух, — `h1` берётся отсюда.
 *
 * Материала у шапки нет: фон, рамку, тень и размытие рисует `Surface` снаружи. В пяти вхождениях
 * кита из девяти в той же стеклянной панели ниже лежит содержимое экрана, поэтому своя панель
 * внутри чужой давала бы вложенное стекло.
 *
 * Крошки в шапку не входят: `Breadcrumbs` — отдельный лендмарк `<nav>`, и кит рисует его
 * над панелью (`Screens` 122-127). Действия на 360px переносятся строкой ниже и не схлопываются
 * в меню: слот принимает `ReactNode` и не знает, что внутри.
 *
 * Контракт: docs/ui-kit/contracts/page-header.md
 */
export function PageHeader({
  heading,
  level = 1,
  variant = 'page',
  eyebrow,
  description,
  status,
  actions,
  children,
  loading = false,
  headingId,
  className,
  ref,
  ...rest
}: PageHeaderProps) {
  const Heading = HEADING_TAG[level]

  // Пустым считается и boolean: идиома `can('cart.manage') && <Button/>` из правила 8
  // отдаёт false, и без проверки обёртка слота рисовалась бы пустой — со щелью и без содержимого
  const hasEyebrow = isFilled(eyebrow)
  const hasDescription = isFilled(description)
  const hasStatus = isFilled(status)
  const hasActions = isFilled(actions)
  // Название объекта приходит с бэка и может не прийти вовсе: пустой h1 в плане заголовков
  // хуже отсутствующего — скринридер объявляет уровень и замолкает
  const hasHeading = isFilled(heading)

  return (
    <header
      {...rest}
      ref={ref}
      className={cx(s.root, VARIANT_CLASS[variant], className)}
      aria-busy={loading || undefined}
      data-variant={variant}
      data-level={level}
      data-loading={loading ? 'true' : undefined}
    >
      <div className={s.main}>
        <div className={s.titleBlock}>
          {hasEyebrow ? <div className={s.eyebrow}>{eyebrow}</div> : null}

          {loading ? (
            /* Заглушки декоративны: о загрузке сказано один раз через aria-busy на корне.
               Обёртки держат высоту строк заголовка и описания, чтобы при подстановке
               данных шапка не прыгала: сам Skeleton ниже обеих строк. */
            <>
              <span className={s.titleSkeleton}>
                <Skeleton shape="line" size="lg" width="long" />
              </span>
              <span className={s.descriptionSkeleton}>
                <Skeleton shape="line" size="md" width="medium" />
              </span>
            </>
          ) : (
            <>
              {hasHeading ? (
                <Heading id={headingId} className={s.title}>
                  {heading}
                </Heading>
              ) : null}
              {hasDescription ? <p className={s.description}>{description}</p> : null}
            </>
          )}
        </div>

        {/* Статус идёт перед действиями и в DOM, и вслух: он относится к объекту из заголовка,
            а кит рисует этот порядок дважды по-разному (Screens 1226 против Screens 551).
            Внутренних обёрток у слотов нет — в ките это один переносящийся ряд капсул. */}
        {hasStatus || hasActions ? (
          <div className={s.aside}>
            {hasStatus ? status : null}
            {hasActions ? actions : null}
          </div>
        ) : null}
      </div>

      {/* Прикреплённая полоса кладётся прямо в корневой столбец: в ките это просто следующие
          строки той же панели с той же щелью, своей обёртки у неё нет */}
      {children}
    </header>
  )
}
